import { SearchspringResultsBase } from '@theme/searchspring-results-base';
import { buildColorLabelToCodeMap, detectColorFromLabels, detectColorGroupFromQuery } from '@theme/searchspring-core';

/**
 * Searchspring Search Results component for search pages.
 * Uses the q parameter for text-based search queries.
 */
class SearchspringSearchResults extends SearchspringResultsBase {
  /**
   * Beacon scope for this surface, read by searchspring-beacon.js.
   * @type {'category' | 'search'}
   */
  surface = 'search';

  /** @type {string} */
  searchTerms = '';

  /** @type {string[] | null} */
  #detectedColorCodes = null;

  /**
   * Builds the full search URL.
   * @param {string} siteId
   * @param {string} searchTerms
   * @param {string} filterQuery
   * @param {string} paginationQuery
   * @param {string} sortQuery
   * @param {string} trackingQuery
   * @param {number} perPage
   * @returns {string}
   */
  static buildSearchUrl(siteId, searchTerms, filterQuery, paginationQuery, sortQuery, trackingQuery, perPage) {
    return `https://${siteId}.a.searchspring.io/api/search/search.json?siteId=${siteId}&q=${encodeURIComponent(searchTerms)}${filterQuery}&resultsFormat=native&beacon=true&resultsPerPage=${perPage}${paginationQuery}${sortQuery}${trackingQuery}`;
  }

  async connectedCallback() {
    this.siteId = this.dataset.siteId || '';
    if (!this.siteId) {
      console.warn('SearchspringSearchResults: missing data-site-id attribute');
      return;
    }

    this.searchTerms = this.dataset.searchTerms || '';

    if (this.searchTerms) {
      this.#detectColors();
    }

    await super.connectedCallback();
  }

  /**
   * Detect color codes from search terms.
   *
   * Color GROUP matching takes priority over individual color labels: a query
   * word like "green" promotes the entire green group (so any product with any
   * green is surfaced), while a specific shade like "agave" falls back to the
   * single matching color. Explicit applied color filters still win over both
   * — see {@link getActiveColorFilters}.
   */
  #detectColors() {
    // 1. Try color group: "green dress" → ["EML","SAG","FRG",...]
    const colorGroupsAttr = this.getAttribute('data-color-groups');
    if (colorGroupsAttr) {
      try {
        /** @type {Record<string, string[]>} */
        const groupsMap = {};
        const raw = JSON.parse(colorGroupsAttr);
        for (const [key, codes] of Object.entries(raw)) {
          groupsMap[key.toLowerCase()] = /** @type {string[]} */ (codes);
        }
        const groupCodes = detectColorGroupFromQuery(this.searchTerms, groupsMap);
        if (groupCodes && groupCodes.length > 0) {
          this.#detectedColorCodes = groupCodes;
          return;
        }
      } catch (e) {
        // ignore parse errors
      }
    }

    // 2. Fall back to an individual color label: "agave" → "AGV"
    const colorLabelsAttr = this.getAttribute('data-color-labels');
    if (colorLabelsAttr) {
      try {
        const colorLabelsArr = JSON.parse(colorLabelsAttr);
        const colorMap = buildColorLabelToCodeMap(colorLabelsArr);
        const singleCode = detectColorFromLabels(this.searchTerms, colorMap);
        if (singleCode) {
          this.#detectedColorCodes = [singleCode];
        }
      } catch (e) {
        // ignore parse errors
      }
    }
  }

  /**
   * @override
   * Include detected color codes when no explicit color filters are applied.
   * @returns {string[]}
   */
  getActiveColorFilters() {
    const explicitFilters = super.getActiveColorFilters();
    if (explicitFilters.length > 0 || !this.#detectedColorCodes) {
      return explicitFilters;
    }
    return this.#detectedColorCodes;
  }

  /**
   * @override
   * @param {Array<{field: string, values: string[]}>} appliedFilters
   * @param {string} trackingQuery
   * @param {number} [page]
   * @returns {string}
   */
  buildApiUrlFor(appliedFilters, trackingQuery, page = this.pagination.currentPage) {
    return SearchspringSearchResults.buildSearchUrl(
      this.siteId,
      this.searchTerms,
      SearchspringResultsBase.buildFilterQuery(appliedFilters),
      SearchspringResultsBase.buildPaginationQuery(page),
      SearchspringResultsBase.buildSortQuery(this.sorting),
      trackingQuery,
      this.pagination.perPage
    );
  }

  /**
   * @override
   * @returns {string}
   */
  getResultsCountLabel() {
    return `${this.pagination.totalResults} Search Results`;
  }

  /**
   * @override
   * @returns {boolean}
   */
  hasRequiredData() {
    return Boolean(this.searchTerms);
  }
}

if (!customElements.get('searchspring-search-results')) {
  customElements.define('searchspring-search-results', SearchspringSearchResults);
}
